import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStorage } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'
import { apiLoginWithPhone } from '../apis/user'
import type { IReqLogin } from '@/types/apis/user'
import { apiGetUserMembershipDetail } from '@/apis/membership'
import type { IResUserMembershipDetail } from '@/types/apis/membership'

export const useUserStore = defineStore('user', () => {
  // 状态
  const isLogin = computed(() => !!token.value)
  const authVersion = ref(0)

  const token = useStorage<string | null>('token', null)
  const profileInfo = ref<IResUserMembershipDetail | null>(null)
  let ensureAuthPending: Promise<boolean> | null = null

  const router = useRouter()
  const route = useRoute()

  const bumpAuthVersion = () => authVersion.value += 1

  // 登录
  async function login(payload: IReqLogin) {
    const res = await apiLoginWithPhone(payload)
    const { Token: newToken } = res.Body
    profileInfo.value = null
    token.value = newToken
    bumpAuthVersion()
    return res
  }

  const setProfileInfo = (detail: IResUserMembershipDetail | null) => profileInfo.value = detail
  const clearProfileInfo = () => profileInfo.value = null

  const refreshProfileInfo = async () => {
    if (!token.value) {
      clearProfileInfo()
      return null
    }

    const res = await apiGetUserMembershipDetail()
    setProfileInfo(res.Body)
    return res.Body
  }

  const ensureAuthReady = async () => {
    if (!token.value) {
      return false
    }

    if (profileInfo.value) {
      return true
    }

    if (ensureAuthPending) {
      return await ensureAuthPending
    }

    ensureAuthPending = (async () => {
      try {
        const detail = await refreshProfileInfo()
        return !!detail
      } finally {
        ensureAuthPending = null
      }
    })()

    return await ensureAuthPending
  }

  // 退出登录
  const logout = (shouldRedirect = true) => {
    token.value = null
    clearProfileInfo()
    bumpAuthVersion()

    if (shouldRedirect) {
      const currentPath = encodeURIComponent(route.fullPath)
      router.replace({
        path: '/login',
        query: { redirect: currentPath },
      })
    }
  }

  return {
    token,
    isLogin,
    authVersion,
    profileInfo,
    login,
    logout,
    setProfileInfo,
    clearProfileInfo,
    refreshProfileInfo,
    ensureAuthReady,
  }
})
